import { AxiosInstance, AxiosResponse } from 'axios';
import { Auth } from '../state/auth/types';
import { get, post } from '../hooks/useAxiosPrivate';
import { DataPayload } from '../state/types';
import {
  AuditActorOption,
  AuditLogEntity,
  AuditTrailFilters,
} from '../state/auditTrail/types';

const companyAuditsUrl = (auth: Auth | null) =>
  `/api/v1/company/${auth?.companyId}/audits`;

const AuditTrailApi = {
  getAuditLogs: async (
    axios: AxiosInstance,
    auth: Auth | null,
    resultsPerPage: number,
    currentPage: number,
    sortColumn: string,
    sortDirection: number,
    filters?: AuditTrailFilters
  ): Promise<DataPayload<AuditLogEntity> & { totalResults?: number }> => {
    const response: AxiosResponse<
      DataPayload<AuditLogEntity> & { totalResults?: number }
    > = await get(axios, companyAuditsUrl(auth), {
      limit: resultsPerPage,
      page: currentPage,
      sortColumn,
      sortDirection,
      filters,
    });
    return response.data;
  },
  getAuditActors: async (
    axios: AxiosInstance,
    auth: Auth | null
  ): Promise<AuditActorOption[]> => {
    const response: AxiosResponse<{ actors: AuditActorOption[] }> = await get(
      axios,
      `${companyAuditsUrl(auth)}/actors`
    );
    return response.data.actors;
  },
  verifyExport: async (
    axios: AxiosInstance,
    auth: Auth | null,
    sha256: string
  ): Promise<{
    recognized: boolean;
    exportedAt?: string;
    exportedBy?: string;
    entityType?: string;
    entityLabel?: string;
  }> => {
    const response = await get(
      axios,
      `${companyAuditsUrl(auth)}/export-verifications`,
      { sha256 }
    );
    return response.data;
  },
  recordPdfExport: async (
    axios: AxiosInstance,
    auth: Auth | null,
    payload: {
      rowCount: number;
      truncated: boolean;
      filters?: AuditTrailFilters;
      includePii: boolean;
    }
  ): Promise<void> => {
    await post(axios, `${companyAuditsUrl(auth)}/export-events`, payload);
  },
  downloadCsv: async (
    axios: AxiosInstance,
    auth: Auth | null,
    filters?: AuditTrailFilters,
    includePii?: boolean
  ): Promise<Blob> => {
    const response: AxiosResponse<Blob> = await post(
      axios,
      `${companyAuditsUrl(auth)}/download`,
      { filters, includePii },
      { responseType: 'blob' }
    );
    return response.data;
  },
};

export default AuditTrailApi;
